import { useForm } from '../../../../hooks';
import React, { useState } from 'react';
import { __ } from '@wordpress/i18n';
import { Modal, Spinner } from '@wordpress/components';
import useDeepCompareEffect from 'use-deep-compare-effect';
import onChange from 'on-change';

interface ConditionalLogicControlModalProps extends BlockProps<any> {
	onRequestClose: () => void;
}

function ConditionalLogicControlModal(
	props: ConditionalLogicControlModalProps
) {
	const form = useForm();
	const { clientId, onRequestClose, setAttributes, attributes } = props;
	const [isClReady, setClReady] = useState(false);

	const gpebObjectType = 'gpeb_' + clientId;

	useDeepCompareEffect(
		() => {
			// Bail if the form is not yet available.
			if (!form.fields?.length) {
				return () => {};
			}

			// Required for the conditional logic UI to work.
			window.form = form;

			const syncClToAttributes = () => {
				setAttributes({
					// Deep clone otherwise we can run into reactivity issues.
					gpebConditionalLogic: JSON.parse(
						JSON.stringify(
							onChange.target(gpebClObj).conditionalLogic
						)
					),
				});
			};

			const gpebClObjBase = {
				conditionalLogic:
					attributes?.gpebConditionalLogic ??
					new (window as any).ConditionalLogic(),
			};

			const gpebClObj = onChange(gpebClObjBase, function () {
				syncClToAttributes();
			});

			window.gform.addFilter(
				'gform_conditional_object',
				function (object, objectType) {
					if (objectType !== gpebObjectType) {
						return object;
					}

					return gpebClObj;
				},
				20,
				gpebObjectType
			);

			window.gform.addFilter(
				'gform_conditional_logic_description',
				function (str, descPieces, objectType, object) {
					if (objectType.indexOf('gpeb_') !== 0) {
						return object;
					}

					descPieces.objectDescription = __(
						'this block if',
						'gp-entry-blocks'
					);

					return Object.values(descPieces).join(' ');
				},
				20,
				gpebObjectType
			);

			// Add "User Role" as a "field"
			window.gform.addFilter(
				'gform_conditional_logic_fields',
				(fields, formId) => {
					// If user_role is already present, don't add it again.
					if (fields.some((field) => field.value === 'user_role')) {
						return fields;
					}

					fields.push({
						label: 'User Role',
						value: 'user_role',
					});

					return fields;
				}
			);

			window.gform.addFilter(
				'gform_conditional_logic_operators',
				(operators, objectType, fieldId) => {
					if (
						objectType !== gpebObjectType ||
						fieldId !== 'user_role'
					) {
						return operators;
					}

					return {
						is: 'is',
						isnot: 'isNot',
					};
				}
			);

			window.gform.addFilter(
				'gform_conditional_logic_values_input',
				(
					str,
					objectType,
					ruleIndex,
					selectedFieldId,
					selectedValue
				) => {
					if (
						objectType !== gpebObjectType ||
						selectedFieldId !== 'user_role'
					) {
						return str;
					}

					str = window.GetRuleValuesDropDown(
						Object.entries(window.GPEB.availableRoles).map(
							([role, label]) => ({
								text: label,
								value: role,
							})
						),
						objectType,
						ruleIndex,
						selectedValue,
						objectType + '_rule_value_' + ruleIndex
					);

					return str;
				}
			);

			window.CreateConditionalLogic(gpebObjectType, gpebClObj);

			//Initializing object so it has the default options set
			window.SetConditionalProperty(
				gpebObjectType,
				'actionType',
				jQuery('#' + gpebObjectType + '_action_type').val()
			);

			window.SetConditionalProperty(
				gpebObjectType,
				'logicType',
				jQuery('#' + gpebObjectType + '_logic_type').val()
			);

			window.SetRule(gpebObjectType, 0);

			setClReady(true);

			return () => {
				window.gform.removeFilter(
					'gform_conditional_object',
					20,
					gpebObjectType
				);

				window.gform.removeFilter(
					'gform_conditional_logic_description',
					20,
					gpebObjectType
				);

				onChange.unsubscribe(gpebClObj);
			};
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- we don't want to include attributes?.gpebConditionalLogic as it will cause an infinite loop.
		[form, gpebObjectType, setAttributes]
	);

	return (
		<Modal
			title={__('Conditional Logic', 'gp-entry-blocks')}
			onRequestClose={onRequestClose}
		>
			<div
				id={`gpeb_${clientId}_conditional_logic_container`}
				style={{ display: isClReady ? 'block' : 'none' }}
			/>

			{!isClReady && <Spinner />}
		</Modal>
	);
}

export default ConditionalLogicControlModal;
